/**
 * Team Management React Query Hooks
 * Provides data fetching and mutation hooks for teams
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { teamApi, type CreateTeamRequest, type UpdateTeamRequest, type AssignEmployeeRequest } from '../api/teams';
import { toast } from 'sonner';

// Query keys for cache management
export const teamKeys = {
  all: ['teams'] as const,
  lists: () => [...teamKeys.all, 'list'] as const,
  list: (page: number, limit: number) => [...teamKeys.lists(), { page, limit }] as const,
  details: () => [...teamKeys.all, 'detail'] as const,
  detail: (id: string) => [...teamKeys.details(), id] as const,
  members: (id: string) => [...teamKeys.all, 'members', id] as const,
};

/**
 * Hook to fetch paginated teams
 */
export function useTeams(page = 1, limit = 20) {
  return useQuery({
    queryKey: teamKeys.list(page, limit),
    queryFn: async () => {
      const response = await teamApi.getTeams(page, limit);
      return response.data;
    },
  });
}

/**
 * Hook to fetch single team by ID
 */
export function useTeam(id: string) {
  return useQuery({
    queryKey: teamKeys.detail(id),
    queryFn: async () => {
      const response = await teamApi.getTeamById(id);
      return response.data;
    },
    enabled: !!id,
  });
}

/**
 * Hook to fetch team members
 */
export function useTeamMembers(teamId: string) {
  return useQuery({
    queryKey: teamKeys.members(teamId),
    queryFn: async () => {
      const response = await teamApi.getTeamMembers(teamId);
      return response.data;
    },
    enabled: !!teamId,
  });
}

/**
 * Hook to create a new team
 */
export function useCreateTeam() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateTeamRequest) => teamApi.createTeam(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: teamKeys.lists() });
      toast.success(response.message || 'Team created successfully');
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to create team';
      toast.error(message);
    },
  });
}

/**
 * Hook to update an existing team
 */
export function useUpdateTeam() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTeamRequest }) =>
      teamApi.updateTeam(id, data),
    onSuccess: (response, variables) => {
      queryClient.invalidateQueries({ queryKey: teamKeys.lists() });
      queryClient.invalidateQueries({ queryKey: teamKeys.detail(variables.id) });
      toast.success(response.message || 'Team updated successfully');
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to update team';
      toast.error(message);
    },
  });
}

/**
 * Hook to delete a team
 */
export function useDeleteTeam() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => teamApi.deleteTeam(id),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: teamKeys.lists() });
      toast.success(response.message || 'Team deleted successfully');
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to delete team';
      toast.error(message);
    },
  });
}

/**
 * Hook to assign employee to team
 */
export function useAssignEmployee() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AssignEmployeeRequest) => teamApi.assignEmployee(data),
    onSuccess: (response, variables) => {
      queryClient.invalidateQueries({ queryKey: teamKeys.lists() });
      if (variables.teamId) {
        queryClient.invalidateQueries({ queryKey: teamKeys.members(variables.teamId) });
      }
      toast.success(response.message || 'Employee assigned successfully');
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to assign employee';
      toast.error(message);
    },
  });
}
